import React from 'react'
import { Modal, Input, Label, Button, Checkbox, Radio, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import type { Coupon } from '../types'
import { upsertCoupon, listAffiliatesBasic, type AffiliateBasic } from '../services/couponsService'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  initial?: Partial<Coupon>
}

function genCode(len = 8): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)]
  return out
}

export default function CouponFormModal({ open, onClose, onSaved, initial }: Props) {
  const { add: toast } = useToast()
  const [name, setName] = React.useState(initial?.name ?? '')
  const [code, setCode] = React.useState(initial?.code ?? '')
  const [discountType, setDiscountType] = React.useState<'percent'|'fixed'>(initial?.discount_type ?? 'percent')
  const [percentOff, setPercentOff] = React.useState<number>(()=> Math.max(0, Math.floor(Number(initial?.percent_off || 0))))
  const [amountCents, setAmountCents] = React.useState<number>(()=> Math.max(0, Math.floor(Number(initial?.amount_off_cents || 0))))
  const [currency, setCurrency] = React.useState<string>((initial?.currency || 'BRL')?.toString().toUpperCase())
  const [startAt, setStartAt] = React.useState<string>(()=> (initial?.start_at ? toLocalDatetime(initial.start_at) : toLocalDatetime(new Date().toISOString())))
  const [noEnd, setNoEnd] = React.useState<boolean>(()=> !initial?.end_at)
  const [endAt, setEndAt] = React.useState<string>(()=> (initial?.end_at ? toLocalDatetime(initial.end_at) : ''))
  const [active, setActive] = React.useState<boolean>(initial?.is_active !== false)
  const [recurring, setRecurring] = React.useState<boolean>(()=> {
    const r = (initial as any)?.recurrence
    return r ? String(r).toLowerCase() !== 'one_time' : false
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [linkAffiliate, setLinkAffiliate] = React.useState<boolean>(()=> !!initial?.affiliate_user_id)
  const [affiliateId, setAffiliateId] = React.useState<string>(()=> initial?.affiliate_user_id || '')
  const [affiliates, setAffiliates] = React.useState<AffiliateBasic[]>([])
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setName(initial?.name ?? '')
    setCode((initial?.code ?? '').toString().toUpperCase())
    setDiscountType((initial?.discount_type as any) || 'percent')
    setPercentOff(Math.max(0, Math.floor(Number(initial?.percent_off || 0))))
    setAmountCents(Math.max(0, Math.floor(Number(initial?.amount_off_cents || 0))))
    setCurrency((initial?.currency || 'BRL')?.toString().toUpperCase())
    setStartAt(initial?.start_at ? toLocalDatetime(initial.start_at) : toLocalDatetime(new Date().toISOString()))
    setNoEnd(!initial?.end_at)
    setEndAt(initial?.end_at ? toLocalDatetime(initial.end_at) : '')
    setActive(initial?.is_active !== false)
    setRecurring((initial as any)?.recurrence ? String((initial as any)?.recurrence).toLowerCase() !== 'one_time' : false)
    setLinkAffiliate(!!initial?.affiliate_user_id)
    setAffiliateId(initial?.affiliate_user_id || '')
    setSaving(false)
    setError(null)
  }, [open, initial])

  React.useEffect(() => { (async () => { try { if (open) { const list = await listAffiliatesBasic(); setAffiliates(list) } } catch {} })() }, [open])
  React.useEffect(() => { if (!open) { setCopied(false) } }, [open])
  React.useEffect(() => { setCopied(false) }, [code])

  function toLocalDatetime(iso: string) {
    try {
      const d = new Date(iso)
      const tz = new Date(d.getTime() - d.getTimezoneOffset()*60000)
      return tz.toISOString().slice(0,16) // yyyy-MM-ddTHH:mm
    } catch { return '' }
  }

  function parseLocalDatetime(local: string): string | null {
    if (!local) return null
    try {
      // Interpret value as local datetime and serialize to UTC ISO (sem ajustar duas vezes)
      const d = new Date(local)
      return d.toISOString()
    } catch { return null }
  }

  function onGenCode() { setCode(genCode()) }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      const startIso = parseLocalDatetime(startAt) || undefined
      const payload = {
        id: initial?.id,
        name: name.trim(),
        code: (code || genCode()).trim().toUpperCase(),
        discount_type: discountType,
        recurrence: (recurring ? 'recurring' : 'one_time') as import('../types').CouponRecurrence,
        percent_off: discountType==='percent' ? Math.max(1, Math.min(99, Math.floor(percentOff||0))) : null,
        amount_off_cents: discountType==='fixed' ? Math.max(0, Math.floor(amountCents||0)) : null,
        currency: discountType==='fixed' ? (currency || 'BRL').toString().toUpperCase() : null,
        start_at: startIso,
        end_at: noEnd ? null : (parseLocalDatetime(endAt) || null),
        is_active: !!active,
        affiliate_user_id: linkAffiliate ? (affiliateId || null) : null,
      }
      await upsertCoupon(payload)
      toast({ title: 'Cupom salvo', description: 'As alterações foram aplicadas com sucesso.', variant: 'success', soft: true })
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.message ?? 'Falha ao salvar cupom')
      toast({ title: 'Falha ao salvar', description: err?.message || 'Não foi possível salvar o cupom.', variant: 'error', soft: true })
    } finally { setSaving(false) }
  }

  return (
    <Modal open={open} onOpenChange={(v)=> { if(!v) onClose() }} className="p-6 sm:p-8">
      <div className="mb-4 text-xl font-semibold">{initial?.id ? 'Editar cupom' : 'Novo cupom'}</div>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
        {error && <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}

        <div className="space-y-1.5">
          <Label>Nome</Label>
          <Input value={name} onChange={(e)=> setName(e.target.value)} placeholder="Ex.: BLACKFRIDAY" required />
        </div>
        <div className="space-y-1.5">
          <Label>Código</Label>
          <div className="flex items-center gap-2">
            <Input value={code} onChange={(e)=> setCode(e.target.value.toUpperCase())} placeholder="Ex.: BF2025" />
            <Button type="button" variant="outline" onClick={onGenCode}>Gerar</Button>
            {/* Copy code */}
            <button
              aria-label="Copiar código"
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-sm hover:bg-muted"
              onClick={async()=>{ try { const txt=(code||'').trim(); if(!txt){ return; } await navigator.clipboard.writeText(txt); setCopied(true); setTimeout(()=> setCopied(false), 1500) } catch {} }}
              title="Copiar código"
            >
              {/* Fallback simples (sem Tooltip para evitar difs por encoding). Ícone via SVG minimal para evitar imports */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </button>
            {copied && <span aria-live="polite" className="text-xs text-emerald-500">Copiado</span>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Tipo de desconto</Label>
          <div className="flex items-center gap-4 text-sm text-foreground/80">
            <label className="inline-flex items-center gap-2"><Radio checked={discountType==='percent'} onChange={()=> setDiscountType('percent')} /> Percentual (%)</label>
            <label className="inline-flex items-center gap-2"><Radio checked={discountType==='fixed'} onChange={()=> setDiscountType('fixed')} /> Valor fixo</label>
          </div>
        </div>
        <div />

        {discountType==='percent' ? (
          <div className="space-y-1.5">
            <Label>Percentual (%)</Label>
            <Input type="number" min="1" max="99" step="1" value={percentOff} onChange={(e)=> setPercentOff(Number(e.target.value)||0)} placeholder="Ex.: 10 para 10%" required />
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label>Valor (centavos)</Label>
              <Input type="number" min="0" step="1" value={amountCents} onChange={(e)=> setAmountCents(Number(e.target.value)||0)} placeholder="Ex.: 1000 para R$10,00" required />
            </div>
            <div className="space-y-1.5">
              <Label>Moeda</Label>
              <select className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm" value={currency} onChange={(e)=> setCurrency(e.target.value)}>
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <Label>Início</Label>
          <Input type="datetime-local" value={startAt} onChange={(e)=> setStartAt((e.target as HTMLInputElement).value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Término</Label>
          <div className="space-y-2">
            <Input type="datetime-local" value={endAt} onChange={(e)=> setEndAt((e.target as HTMLInputElement).value)} disabled={noEnd} />
            <label className="flex items-center gap-2 text-sm text-foreground/80"><Checkbox checked={noEnd} onChange={(e)=> setNoEnd((e.target as HTMLInputElement).checked)} /> Sem data de término</label>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Ativo</Label>
          <div className="flex items-center gap-2"><Switch checked={active} onChange={(e)=> setActive((e.target as HTMLInputElement).checked)} /> <span className="text-sm">Cupom ativo</span></div>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Aplicação</Label>
          <div className="flex items-center gap-2">
            <Switch checked={recurring} onChange={(e)=> setRecurring((e.target as HTMLInputElement).checked)} />
            <span className="text-sm">Aplicar em todas as parcelas</span>
          </div>
          <p className="text-xs text-foreground/70">Desligue para aplicar apenas na primeira mensalidade.</p>
        </div>

        <div className="space-y-1.5 sm:col-span-2 border-t border-border pt-2 mt-2">
          <Label>Vincular a afiliado</Label>
          <div className="flex items-center gap-2"><Checkbox checked={linkAffiliate} onChange={(e)=> setLinkAffiliate((e.target as HTMLInputElement).checked)} /> <span className="text-sm">Associar este cupom a um afiliado</span></div>
          {linkAffiliate && (
            <div className="pt-2">
              <Label className="text-xs text-foreground/70">Selecionar afiliado</Label>
              <select className="mt-1 h-12 w-full rounded-md border border-input bg-background px-3 text-sm" value={affiliateId} onChange={(e)=> setAffiliateId(e.target.value)} required>
                <option value="">Selecione...</option>
                {affiliates.map((a)=> (
                  <option key={a.id} value={a.id}>{a.name || a.email || a.code || a.id}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar cupom'}</Button>
        </div>
      </form>
    </Modal>
  )
}
